import {
    attachmentService,
    AuthContext,
    gitlabStorageService
} from "@harvestr-api/common";
import {
    gqlAttachment,
    gqlDiscovery,
    gqlGitlabIssue,
    gqlGitlabIssueCreateWithoutDiscoveryInput
} from "@harvestr-api/models";
import {
    htmlToMarkdown,
    httpRequestPaginated,
    parallelPromiseHelper,
    toClientId
} from "@harvestr-api/utils";
import * as request from "request-promise";
import {
    getGitlabBearer,
    getGitlabToken,
    GITLAB_CLOUD_API_URL
} from "../helpers/gitlab-auth.helper";
import {
    GitlabGroup,
    GitlabProject,
    GitlabSingleIssue
} from "../models/gitlab";

export const gitlabApiService = {
    async createIssue(
        data: {
            harvestrIssue: gqlGitlabIssueCreateWithoutDiscoveryInput;
            discovery: Pick<gqlDiscovery, "id" | "title" | "clientId">;
            copyDiscoveryAttachments: boolean;
        },
        ctx: AuthContext
    ): Promise<gqlGitlabIssueCreateWithoutDiscoveryInput> {
        const { auth, host_url } = await getGitlabBearer(ctx);

        const validatedGitlabIssue = gitlabApiService.validateGithubIssue(
            data.harvestrIssue
        );

        const created_gitlab_issue = await request.post({
            url: `${host_url}/projects/${validatedGitlabIssue.gitlabProjectId}/issues`,
            auth,
            form: gitlabApiService.toGitlabIssue(validatedGitlabIssue),
            json: true
        });

        const convertedIssue = gitlabApiService.toHarvestrGitlabIssue(
            created_gitlab_issue,
            validatedGitlabIssue
        );

        await gitlabApiService.linkIssue(
            {
                harvestrIssue: {
                    ...validatedGitlabIssue,
                    ...convertedIssue
                },
                discovery: data?.discovery,
                copyDiscoveryAttachments: data.copyDiscoveryAttachments
            },
            ctx
        );
        return convertedIssue;
    },

    async linkIssue(
        data: {
            harvestrIssue: gqlGitlabIssueCreateWithoutDiscoveryInput;
            discovery: Pick<gqlDiscovery, "id" | "title" | "attachments">;
            copyDiscoveryAttachments: boolean;
        },
        ctx: AuthContext
    ) {
        const linkToDiscovery = {
            filename: data?.discovery?.title,
            url: `${process.env.H_CLIENT_URL}/components/0/list/${
                toClientId(data?.discovery)?.id
            }`,
            body: `**Issue was linked to Harvestr Discovery:**  [${
                data?.discovery?.title || ""
            }](${process.env.H_CLIENT_URL}/components/0/list/${
                toClientId(data?.discovery)?.id
            })`
        };

        const setupDiscoveryAttachments = async () => {
            if (data.copyDiscoveryAttachments === false) {
                return [];
            }
            const discoveryAttachments: Array<gqlAttachment> = await attachmentService.getAttachmentsOfDiscovery(
                { discoveryId: data.discovery.id }
            );

            return discoveryAttachments.map(attachment => {
                return {
                    filename: attachment?.filename,
                    url: attachment?.url,
                    body: `**Harvestr discovery file-attachment:**  [${
                        attachment?.filename || ""
                    }](${attachment?.url})`
                };
            });
        };

        const listCommentsOfCard = await gitlabApiService.getCommentsOfIssue(
            {
                issueIid: data?.harvestrIssue.gitlabIid,
                projectId: data?.harvestrIssue?.gitlabProjectId
            },
            ctx
        );

        const candidateAttachments = [
            linkToDiscovery,
            ...(await setupDiscoveryAttachments())
        ].filter(candidateAttachment => {
            const attachmentAlreadySet = listCommentsOfCard.some(comment => {
                return (
                    comment.body.includes(candidateAttachment.url) &&
                    comment.body.includes(candidateAttachment.filename)
                );
            });
            return attachmentAlreadySet === false;
        });
        await parallelPromiseHelper.uploadData(
            candidateAttachments || [],
            async attachmentsChunk => {
                return (attachmentsChunk || []).map(attachment => {
                    return gitlabApiService.createComment(
                        {
                            project_id: data?.harvestrIssue?.gitlabProjectId,
                            issue_iid: data?.harvestrIssue?.gitlabIid,
                            comment: {
                                body: attachment.body
                            }
                        },
                        ctx
                    );
                });
            },
            5
        );

        const remoteGitlabIssue = await gitlabApiService.getIssue(
            {
                issue_iid: data?.harvestrIssue?.gitlabIid,
                project_id: data?.harvestrIssue?.gitlabProjectId
            },
            ctx
        );
        const fullHarvestrGitlabIssue = gitlabApiService.toHarvestrGitlabIssue(
            remoteGitlabIssue,
            data?.harvestrIssue
        );
        return fullHarvestrGitlabIssue;
    },

    async updateIssue(
        data: {
            project_id: number;
            issue_iid: number;
            issue: {
                title?: string;
                description?: string;
                labels?: string;
            };
        },
        ctx: AuthContext
    ) {
        const { auth, host_url } = await getGitlabBearer(ctx);
        return request.put({
            url: `${host_url}/projects/${data.project_id}/issues/${data.issue_iid}`,
            auth,
            form: { labels: "Harvestr" },
            json: true
        });
    },
    async createComment(
        data: {
            project_id: number;
            issue_iid: number;
            comment: {
                body: string;
            };
        },
        ctx: AuthContext
    ) {
        const { auth, host_url } = await getGitlabBearer(ctx);
        return request.post({
            url: `${host_url}/projects/${data.project_id}/issues/${data.issue_iid}/notes`,
            auth,
            form: data.comment,
            json: true
        });
    },
    async getGroups(
        ctx: AuthContext,
        gotTokenAlready?: string,
        gotHostUrlAlready?: string
    ): Promise<GitlabGroup[]> {
        const { auth, host_url } = await getGitlabBearer(
            ctx,
            gotTokenAlready,
            gotHostUrlAlready
        );
        return request.get({
            url: `${host_url}/groups`,
            auth,
            qs: {},
            json: true
        });
    },

    async getCommentsOfIssue(
        where: { issueIid: number; projectId: number },
        ctx: AuthContext
    ) {
        const { auth, host_url } = await getGitlabBearer(ctx);
        return request.get({
            url: `${host_url}/projects/${where.projectId}/issues/${where.issueIid}/notes`,
            auth,
            qs: {},
            json: true
        });
    },

    async getUser(
        ctx: AuthContext,
        gotTokenAlready?: string,
        gotHostUrlAlready?: string
    ) {
        const { auth, host_url } = await getGitlabBearer(
            ctx,
            gotTokenAlready,
            gotHostUrlAlready
        );
        return request.get({
            url: `${host_url}/user`,
            auth,
            qs: {},
            json: true
        });
    },

    async hasUserRequiredPermissions(
        where: { user?: any },
        ctx: AuthContext,
        gotTokenAlready?: string,
        gotHostUrlAlready?: string
    ) {
        const GITLAB_USER_MIN_ACCESS_LEVEL_ALLOWED = 30;
        const userAccessLevel: number[] = [];
        const groups = await gitlabApiService.getGroups(
            ctx,
            gotTokenAlready,
            gotHostUrlAlready
        );
        const user =
            where?.user ||
            (await gitlabApiService.getUser(
                ctx,
                gotTokenAlready,
                gotHostUrlAlready
            ));
        for (const group of groups) {
            try {
                const userWithPerms = await gitlabApiService.getUserPermissions(
                    {
                        user,
                        group_id: group.id
                    },
                    ctx,
                    gotTokenAlready,
                    gotHostUrlAlready
                );
                userAccessLevel.push(userWithPerms?.access_level || 0);
            } catch (error) {
                if (error.statusCode == 404) {
                    console.log(`User does not belong to group ${group.id}`);
                }
            }
        }

        const hasRequiredPermission: boolean = userAccessLevel.every(
            level => level >= Number(GITLAB_USER_MIN_ACCESS_LEVEL_ALLOWED)
        );
        return hasRequiredPermission;
    },

    async getUserPermissions(
        where: {
            user?: any;
            group_id: number;
        },
        ctx: AuthContext,
        gotTokenAlready?: string,
        gotHostUrlAlready?: string
    ) {
        const { auth, host_url } = await getGitlabBearer(
            ctx,
            gotTokenAlready,
            gotHostUrlAlready
        );
        const user =
            where.user ||
            (await gitlabApiService.getUser(
                ctx,
                gotTokenAlready,
                gotHostUrlAlready
            ));
        return request.get({
            url: `${host_url}/groups/${where.group_id}/members/${user.id}`,
            auth,
            qs: {},
            json: true
        });
    },

    async getProjects(
        data: { group_id: number },
        ctx: AuthContext,
        gotTokenAlready?: string,
        gotHostUrlAlready?: string
    ): Promise<GitlabProject[]> {
        const httpRequest = async (
            pageNumber: number,
            itemsPerPage: number
        ): Promise<GitlabProject[]> => {
            const { auth, host_url } = await getGitlabBearer(
                ctx,
                gotTokenAlready,
                gotHostUrlAlready
            );
            return request.get({
                url: `${host_url}/groups/${data?.group_id}/projects?page=${pageNumber}&per_page=${itemsPerPage}`,
                auth,
                qs: {},
                json: true
            });
        };

        return httpRequestPaginated<GitlabProject>({
            httpRequest,
            pageStartsAt: 1,
            itemsPerPage: 100
        });
    },
    async getProject(
        data: { project_id: number },
        ctx: AuthContext
    ): Promise<GitlabProject> {
        const { auth, host_url } = await getGitlabBearer(ctx);
        return request.get({
            url: `${host_url}/projects/${data?.project_id}`,
            auth,
            qs: {},
            json: true
        });
    },

    async getProjectBoards(
        data: { project_id: number },
        ctx: AuthContext
    ): Promise<any[]> {
        const { auth, host_url } = await getGitlabBearer(ctx);
        const httpRequest = async (
            pageNumber: number,
            itemsPerPage: number
        ): Promise<any[]> => {
            return request.get({
                url: `${host_url}/projects/${data?.project_id}/boards?page=${pageNumber}&per_page=${itemsPerPage}`,
                auth,
                qs: {},
                json: true
            });
        };

        return httpRequestPaginated<any>({
            httpRequest,
            pageStartsAt: 1,
            itemsPerPage: 100
        });
    },

    async getBoardColumns(
        data: { project_id: number; board_id: number },
        ctx: AuthContext
    ) {
        const { auth, host_url } = await getGitlabBearer(ctx);
        const httpRequest = async (
            pageNumber: number,
            itemsPerPage: number
        ): Promise<any[]> => {
            return request.get({
                url: `${host_url}/projects/${data?.project_id}/boards/${data?.board_id}/lists?page=${pageNumber}&per_page=${itemsPerPage}`,
                auth,
                qs: {},
                json: true
            });
        };
        return httpRequestPaginated<any>({
            httpRequest,
            pageStartsAt: 1,
            itemsPerPage: 100
        });
    },

    async getGroupBoards(
        data: { group_id: number },
        ctx: AuthContext
    ): Promise<any> {
        const { auth, host_url } = await getGitlabBearer(ctx);
        const httpRequest = async (
            pageNumber: number,
            itemsPerPage: number
        ): Promise<any[]> => {
            return request.get({
                url: `${host_url}/groups/${data?.group_id}/boards?page=${pageNumber}&per_page=${itemsPerPage}`,
                auth,
                qs: {},
                json: true
            });
        };

        return httpRequestPaginated<any>({
            httpRequest,
            pageStartsAt: 1,
            itemsPerPage: 100
        });
    },

    async getProjectUsers(
        data: { project_id: number },
        ctx: AuthContext
    ): Promise<any[]> {
        const { auth, host_url } = await getGitlabBearer(ctx);
        const httpRequest = async (
            pageNumber: number,
            itemsPerPage: number
        ): Promise<any[]> => {
            return request.get({
                url: `${host_url}/projects/${data?.project_id}/users?page=${pageNumber}&per_page=${itemsPerPage}`,
                auth,
                qs: {},
                json: true
            });
        };

        return httpRequestPaginated<any>({
            httpRequest,
            pageStartsAt: 1,
            itemsPerPage: 100
        });
    },

    async searchIssues(
        data: { project_id: number; query: string },
        ctx: AuthContext
    ) {
        const { auth, host_url } = await getGitlabBearer(ctx);
        const httpRequest = async (
            pageNumber: number,
            itemsPerPage: number
        ): Promise<any[]> => {
            return request.get({
                url: `${host_url}/projects/${data?.project_id}/issues?page=${pageNumber}&per_page=${itemsPerPage}`,
                auth,
                qs: {
                    state: "opened",
                    search: data.query
                },
                json: true
            });
        };

        return httpRequestPaginated<any>({
            httpRequest,
            pageStartsAt: 1,
            itemsPerPage: 100
        });
    },

    async getIssue(
        data: { project_id: number; issue_iid: number },
        ctx: AuthContext
    ): Promise<GitlabSingleIssue> {
        const { auth, host_url } = await getGitlabBearer(ctx);
        return request.get({
            url: `${host_url}/projects/${data?.project_id}/issues/${data.issue_iid}`,
            auth,
            qs: {},
            json: true
        });
    },

    async getLabels(data: { project_id: number }, ctx: AuthContext) {
        const httpRequest = async (
            pageNumber: number,
            itemsPerPage: number
        ): Promise<any[]> => {
            const { auth, host_url } = await getGitlabBearer(ctx);
            return request.get({
                url: `${host_url}/projects/${data?.project_id}/labels?page=${pageNumber}&per_page=${itemsPerPage}`,
                auth,
                qs: {},
                json: true
            });
        };

        return httpRequestPaginated<any>({
            httpRequest,
            pageStartsAt: 1,
            itemsPerPage: 100
        });
    },

    async searchMilestones(
        data: { project_id: number; query: string },
        ctx: AuthContext
    ) {
        const { auth, host_url } = await getGitlabBearer(ctx);
        const httpRequest = async (
            pageNumber: number,
            itemsPerPage: number
        ): Promise<any[]> => {
            return request.get({
                url: `${host_url}/projects/${data?.project_id}/milestones?page=${pageNumber}&per_page=${itemsPerPage}`,
                auth,
                qs: {
                    state: "active",
                    search: data.query
                },
                json: true
            });
        };

        return httpRequestPaginated<any>({
            httpRequest,
            pageStartsAt: 1,
            itemsPerPage: 100
        });
    },

    async getMilestone(
        data: { project_id: number; milestone_id: number },
        ctx: AuthContext
    ): Promise<any> {
        const { auth, host_url } = await getGitlabBearer(ctx);
        return request.get({
            url: `${host_url}/projects/${data?.project_id}/milestones/${data.milestone_id}`,
            auth,
            qs: {},
            json: true
        });
    },

    async hasIntegrationEnabled(projectId: string): Promise<boolean> {
        const integrationToken = await getGitlabToken({
            project_id: projectId,
            type: "INTEGRATION_GITLAB"
        });
        return integrationToken !== undefined;
    },

    async syncIssues(
        where: { projectsIds: string[] },
        apis: {
            devtools: {
                canSyncToday: (
                    devtoolName: string,
                    redisKey: string
                ) => Promise<boolean>;
                BASE_REDIS_ISSUES_SYNC_KEY: Readonly<string>;
            };
        }
    ) {
        console.log("gitlab.syncIssues() --------------------");

        console.log(
            "gitlab.syncIssues() : where.projectsIds : ",
            where.projectsIds
        );

        const tasks = where.projectsIds.map(async projectId => {
            const integrationStateEnabled = await gitlabApiService.hasIntegrationEnabled(
                projectId
            );
            if (integrationStateEnabled === false) {
                return [];
            } else {
                const REDIS_KEY_LAST_SYNC = `${apis.devtools.BASE_REDIS_ISSUES_SYNC_KEY}${projectId}`;
                const canSyncNow = await apis.devtools.canSyncToday(
                    "gitlab",
                    REDIS_KEY_LAST_SYNC
                );
                if (canSyncNow === false) {
                    console.log(
                        `gitlab : sync OFF for projectId ${projectId} : last sync less than 24h`
                    );
                    return [];
                } else {
                    console.log(`gitlab : sync ON for projectId ${projectId}`);
                    const issues = (
                        await gitlabStorageService.getIssuesOfProjectForSync({
                            projectId
                        })
                    ).filter(
                        issue =>
                            String(issue.gitlabId) !== "0" &&
                            issue.gitlabId !== null &&
                            String(issue.gitlabProjectId) !== "-1"
                    );

                    return gitlabApiService.bulkSyncIssuesOfProjects(issues, {
                        project_id: projectId,
                        type: "INTEGRATION_GITLAB"
                    });
                }
            }
        });

        console.log("gitlab.syncIssues(): END");
        return tasks;
    },

    async bulkSyncIssuesOfProjects(
        hvrIssues: gqlGitlabIssue[],
        ctx: AuthContext
    ) {
        return parallelPromiseHelper.uploadData(
            hvrIssues,
            async hvrIssuesChunk => {
                return hvrIssuesChunk.map(async hvrIssue => {
                    try {
                        const issueFromGitlab: any = await gitlabApiService.getIssue(
                            {
                                project_id: hvrIssue.gitlabProjectId,
                                issue_iid: hvrIssue.gitlabIid
                            },
                            {
                                project_id: ctx.project_id,
                                type: "INTEGRATION_GITLAB"
                            }
                        );
                        if (
                            issueFromGitlab &&
                            issueFromGitlab?.id &&
                            issueFromGitlab?.gitlabIid &&
                            issueFromGitlab?.gitlabIid > 0 &&
                            typeof issueFromGitlab === "object"
                        ) {
                            return gitlabStorageService.updateIssueByGitlabId(
                                issueFromGitlab.id,
                                gitlabApiService.toHarvestrGitlabIssue(
                                    issueFromGitlab
                                )
                            );
                        } else {
                            return Promise.resolve();
                        }
                    } catch (error) {
                        if (
                            error.statusCode === 401 ||
                            error.statusCode === 404 ||
                            error.statusCode === 429
                        ) {
                            console.log(
                                "issue not found or over-rate-limit: gitlabIid : ",
                                hvrIssue.gitlabIid
                            );
                            return false;
                        }
                    }
                });
            }
        );
    },

    toHarvestrGitlabIssue(
        gitlabIssue,
        harvestrIssue?: gqlGitlabIssueCreateWithoutDiscoveryInput
    ): gqlGitlabIssueCreateWithoutDiscoveryInput {
        return {
            id: harvestrIssue?.id,
            gitlabId: gitlabIssue?.id,
            gitlabIid: gitlabIssue?.iid,
            gitlabUrl: gitlabIssue?.web_url,
            gitlabProjectId:
                harvestrIssue?.gitlabProjectId || gitlabIssue?.project_id || -1,
            gitlabProjectName: harvestrIssue?.gitlabProjectName || "",
            gitlabTitle: gitlabIssue?.title || "",
            gitlabDescription: gitlabIssue?.description || "",
            gitlabStateName: gitlabIssue?.state || "opened",

            gitlabBoardId: harvestrIssue?.gitlabBoardId || -1,
            gitlabBoardName: harvestrIssue?.gitlabBoardName || "",
            gitlabListId: harvestrIssue?.gitlabListId || -1,
            gitlabListName: harvestrIssue?.gitlabListName || "",
            gitlabListPosition: harvestrIssue?.gitlabListPosition || -1,
            gitlabLabels: Array.from(
                new Set(
                    [
                        ...(harvestrIssue?.gitlabLabels || []),
                        ...(gitlabIssue?.labels || [])
                    ].filter(l => l !== null)
                )
            ),
            gitlabMilestoneId: gitlabIssue?.milestone?.id || 0,
            gitlabMilestoneIid: gitlabIssue?.milestone?.iid || 0,
            gitlabMilestoneTitle: gitlabIssue?.milestone?.title || "",
            gitlabMilestoneDescription:
                gitlabIssue?.milestone?.description || "",
            gitlabAuthorId: gitlabIssue?.author?.id || -1,
            gitlabAuthorUsername: gitlabIssue?.author?.username || "",
            gitlabAuthorName: gitlabIssue?.author?.name || "",
            gitlabAssigneeId: gitlabIssue?.assignee?.id || -1,
            gitlabAssigneeName: gitlabIssue?.assignee?.name || "",
            gitlabAssigneeUsername: gitlabIssue?.assignee?.username || "",
            gitlabAssigneeUrl: gitlabIssue?.assignee?.web_url || "",
            gitlabTimeEstimate: gitlabIssue?.time_stats?.time_estimate || 0,
            gitlabTotalTimeSpent:
                gitlabIssue?.time_stats?.total_time_spent || 0,
            gitlabHasTasks: false
        };
    },

    validateGithubIssue(
        gitlabIssue: gqlGitlabIssueCreateWithoutDiscoveryInput
    ): gqlGitlabIssueCreateWithoutDiscoveryInput {
        const markdownService = htmlToMarkdown({
            rules: {
                weblink: {
                    filter: ["a"],
                    replacement: (content, domNode) =>
                        `[${content}](${domNode.getAttribute("href")})`
                },
                strikethrough: {
                    filter: ["s"],
                    replacement: content => `~~${content}~~`
                },
                bold: {
                    filter: ["strong", "b"],
                    replacement: content => `**${content}**`
                },
                heading1: {
                    filter: ["h1"],
                    replacement: content => `# ${content}`
                },
                heading2: {
                    filter: ["h2"],
                    replacement: content => `## ${content}`
                }
            }
        });
        return {
            ...gitlabIssue,
            gitlabDescription: markdownService.htmlToMarkdown(
                gitlabIssue?.gitlabDescription || ""
            )
        };
    },

    toGitlabIssue: (
        harvestrIssue: gqlGitlabIssueCreateWithoutDiscoveryInput
    ) => ({
        project_id: harvestrIssue?.gitlabProjectId || -1,
        title: harvestrIssue?.gitlabTitle || "",
        description: harvestrIssue?.gitlabDescription || "",
        milestone_id: harvestrIssue?.gitlabMilestoneId || -1,
        labels: (harvestrIssue?.gitlabLabels || []).join(","),
        assignee_id: harvestrIssue?.gitlabAssigneeId || 0
    }),

    CLOUD_API_URL: GITLAB_CLOUD_API_URL
};
